import type {
	AdminPermission,
	AuthMode,
	CachedSession,
	User,
} from 'shared/store/auth'

export const LEGACY_AUTH_KEY = '@auth'
export const TELEGRAM_AUTH_KEY = '@auth:telegram:v3'
export const BROWSER_ADMIN_AUTH_KEY = '@auth:browser-admin:v3'

const TELEGRAM_AUTH_KEY_V2 = '@auth:telegram:v2'
const BROWSER_ADMIN_AUTH_KEY_V2 = '@auth:browser-admin:v2'
const ADMIN_PERMISSIONS = [
	'super_admin',
	'finance_admin',
	'lots_admin',
	'seller',
] as const

function storageKey(mode: AuthMode): string {
	return mode === 'telegram' ? TELEGRAM_AUTH_KEY : BROWSER_ADMIN_AUTH_KEY
}

function isUser(value: unknown): value is User {
	if (!value || typeof value !== 'object') return false
	const user = value as Partial<User>
	if (!Array.isArray(user.adminPermissions)) return false
	if (
		!user.adminPermissions.every((permission) =>
			ADMIN_PERMISSIONS.includes(permission as AdminPermission),
		)
	)
		return false
	if (user.role === 'user' && user.adminPermissions.length !== 0) return false
	if (user.role === 'admin' && user.adminPermissions.length === 0) return false
	return (
		typeof user._id === 'string' &&
		typeof user.name === 'string' &&
		(user.role === 'admin' || user.role === 'user')
	)
}

function parseSession(
	value: string | null,
	mode: AuthMode,
): CachedSession | null {
	if (!value) return null
	try {
		const parsed = JSON.parse(value) as Partial<CachedSession>
		if (
			parsed.version !== 3 ||
			parsed.mode !== mode ||
			typeof parsed.token !== 'string' ||
			!parsed.token ||
			typeof parsed.expiresAt !== 'number' ||
			!Number.isFinite(parsed.expiresAt) ||
			!isUser(parsed.user)
		) {
			return null
		}
		// Dev override sessions may impersonate users without a telegramId.
		if (
			mode === 'telegram' &&
			!parsed.isDevOverride &&
			(typeof parsed.telegramId !== 'number' ||
				parsed.user.telegramId !== parsed.telegramId)
		) {
			return null
		}
		if (mode === 'browser-admin' && parsed.telegramId !== undefined) return null
		return parsed as CachedSession
	} catch {
		return null
	}
}

export function migrateLegacyAuth(): void {
	localStorage.removeItem(LEGACY_AUTH_KEY)
	localStorage.removeItem(TELEGRAM_AUTH_KEY_V2)
	localStorage.removeItem(BROWSER_ADMIN_AUTH_KEY_V2)
}

export function readCachedSession(
	mode: AuthMode,
	currentTelegramId?: number,
	now = Date.now(),
	isDevelopment = import.meta.env.DEV,
): CachedSession | null {
	const key = storageKey(mode)
	const session = parseSession(localStorage.getItem(key), mode)
	const invalid =
		!session ||
		session.expiresAt <= now ||
		// Dev override sessions intentionally impersonate different users —
		// skip the telegramId mismatch guard for them.
		(!session.isDevOverride &&
			!isDevelopment &&
			mode === 'telegram' &&
			session.telegramId !== currentTelegramId)
	if (invalid) {
		localStorage.removeItem(key)
		return null
	}
	return session
}

export function writeCachedSession(session: CachedSession): void {
	localStorage.setItem(storageKey(session.mode), JSON.stringify(session))
}

export function removeCachedSession(mode: AuthMode): void {
	localStorage.removeItem(storageKey(mode))
}
